import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import {
  NumberInputControl,
  NumberInputDecrementTrigger,
  NumberInputField,
  NumberInputIncrementTrigger,
  NumberInputRoot,
} from '@/components/ui/number-input';
import { Box, HStack, Input, Text } from '@chakra-ui/react';
import { COLLAB_SETTLEABLE_CURRENCIES, type BriefFixedPayment } from '@fluenceur/types';
import { NavArrowDown } from 'iconoir-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencySymbol } from '../../_shared/format-money';
import { useDecimalInputLocale } from '@/hooks/use-decimal-number-input';

export type MoneyAmountInputVariant = 'text' | 'number';

interface MoneyAmountFieldProps {
  value: BriefFixedPayment | null;
  /**
   * `null` means no amount agreed yet — an empty box, not a deal at zero. The
   * field never emits an amount of zero, so no intermediate state of an honest
   * edit can be refused on its way to the server.
   */
  onChange: (value: BriefFixedPayment | null) => void;
  hasError: boolean;
  /**
   * What the caller may denominate this amount in. Defaults to what the payment
   * rail can settle, because the first caller was a payment. A gifted product's
   * value is not paid by anyone, so its caller passes the brand's own currency
   * instead — the constraint belongs to the caller, not to the box.
   */
  currencies?: readonly string[];
  /** Quiet line under the box. Defaults to how the creator gets paid. */
  note?: ReactNode;
  placeholder?: string;
  /**
   * Prefix of the two test ids. Left on the payment value by default so the
   * suites that predate the generalization read unchanged.
   */
  testIdPrefix?: string;
  /** Flows opts into numeric controls; the manual brief editor retains its text input. */
  amountInputVariant?: MoneyAmountInputVariant;
}

const toCents = (raw: string) => {
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
};

const toInput = (amountCents: number) => (amountCents > 0 ? String(amountCents / 100) : '');

const currencyLabel = (code: string) => `${code} ${currencySymbol(code)}`;

/**
 * The currency side of the box. A single allowed currency is stated, not
 * offered: a menu that opens on one line, identical to the label above it, is a
 * gesture that leads nowhere. As soon as a second currency is allowed the same
 * control becomes the picker again, with no other change to make.
 */
const CurrencyControl = ({
  currency,
  currencies,
  onSelect,
  testId,
}: {
  currency: string;
  currencies: readonly string[];
  onSelect: (next: string) => void;
  testId: string;
}) => {
  const frame = {
    px: '3',
    h: 'full',
    gap: '1',
    borderLeftWidth: '1px',
    borderLeftStyle: 'dashed',
    borderColor: 'perRecipient.borderSoft',
    color: 'perRecipient.textMuted',
    fontSize: '13px',
    fontWeight: 'semibold',
  } as const;

  if (currencies.length < 2) {
    return (
      <HStack {...frame} data-testid={testId}>
        <Text>{currency}</Text>
      </HStack>
    );
  }

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }}>
      <MenuTrigger asChild>
        <HStack as="button" {...frame} data-testid={testId}>
          <Text>{currency}</Text>
          <NavArrowDown width={13} height={13} />
        </HStack>
      </MenuTrigger>
      {/* Inline (no body portal): the compensation step reuses this field
          inside the send modal's Dialog, whose focus trap otherwise swallows
          a body-portaled item click. Safe on the full-page editor too — the
          positioner still floats above the field. Mirrors TemplatePicker. */}
      <MenuContent minW="120px" portalled={false}>
        {currencies.map((code) => (
          <MenuItem key={code} value={code} onSelect={() => onSelect(code)}>
            {currencyLabel(code)}
          </MenuItem>
        ))}
      </MenuContent>
    </MenuRoot>
  );
};

/**
 * Amount + currency control, styled as a per-recipient (blue dashed) group. The
 * amount is entered in whole currency units and stored back as integer cents.
 * It denominates a fixed payment, and — with a caller-supplied currency list
 * and note — the value of a gifted product, which is a worth, never a sum
 * anyone will receive.
 *
 * The manual editor retains its text input. Flows opts into the shared
 * NumberInput, with raw draft text kept until the parent acknowledges cents.
 * Either decimal separator is accepted; only cents conversion normalizes it.
 * Cents are derived from the typed text; they never rebuild partial input.
 * An empty or zero amount emits null, preserving the picked currency locally.
 */
export const MoneyAmountField = ({
  value,
  onChange,
  hasError,
  currencies = COLLAB_SETTLEABLE_CURRENCIES,
  note,
  placeholder,
  testIdPrefix = 'fixed-payment',
  amountInputVariant = 'text',
}: MoneyAmountFieldProps) => {
  const { t } = useTranslation();
  // A draft written before the rail closed the door can still carry a currency
  // nothing can pay, and a value saved under a currency the brand no longer
  // works in is the same situation. The box states the currency this brief will
  // be saved in, and the next edit commits it, rather than showing a promise
  // the backend would refuse.
  const stored = value?.currency;
  const storedCurrency = stored && currencies.includes(stored) ? stored : null;
  const amountCents = value?.amountCents ?? 0;

  const [amount, setAmount] = useState(() => toInput(amountCents));
  const decimal = useDecimalInputLocale('en-US', false);
  const [syncedCents, setSyncedCents] = useState(amountCents);
  const incomingCents = useRef(amountCents);
  const pendingCents = useRef<number[]>([]);
  // The currency outlives the amount. No amount agreed is emitted as `null`,
  // which carries no currency back, so the choice lives here: picking one
  // before typing a figure, or clearing a figure to correct it, must not
  // silently return the brief to the default currency.
  const [currency, setCurrency] = useState(storedCurrency ?? currencies[0]);
  const [syncedCurrency, setSyncedCurrency] = useState(storedCurrency);

  // Adopt an amount the parent replaced on its own (hydrating a template, a
  // reset). Equal cents mean the box holds the fresher text — a half-typed
  // decimal reads as the same amount — so it stands.
  if (amountInputVariant === 'number' && amountCents !== incomingCents.current) {
    incomingCents.current = amountCents;
    const echo = pendingCents.current.indexOf(amountCents);
    if (echo >= 0) pendingCents.current.splice(0, echo + 1);
    else {
      pendingCents.current = [];
      setAmount(toInput(amountCents));
      decimal.resetLocale('en-US');
    }
  } else if (amountInputVariant === 'text' && amountCents !== syncedCents) {
    setSyncedCents(amountCents);
    if (toCents(amount) !== amountCents) setAmount(toInput(amountCents));
  }

  // Same adoption for the currency, one-way: an allowed currency handed over
  // by the parent wins, its disappearance (no amount left) changes nothing.
  if (storedCurrency !== syncedCurrency) {
    setSyncedCurrency(storedCurrency);
    if (storedCurrency) setCurrency(storedCurrency);
  }

  const commitAmount = (raw: string) => {
    if (!/^\d*(?:[.,]\d*)?$/.test(raw)) return;
    const nextCents = toCents(raw);
    setAmount(raw);
    if (amountInputVariant === 'number') {
      const latest = pendingCents.current.length
        ? pendingCents.current.at(-1)
        : incomingCents.current;
      if (latest !== nextCents) {
        pendingCents.current.push(nextCents);
      }
    } else setSyncedCents(nextCents);
    onChange(nextCents > 0 ? { amountCents: nextCents, currency } : null);
  };

  const selectCurrency = (next: string) => {
    setCurrency(next);
    // Nothing is agreed until an amount exists: the pick is remembered by the
    // box and committed with the first figure typed.
    const currentCents = amountInputVariant === 'number' ? toCents(amount) : amountCents;
    if (currentCents > 0) onChange({ amountCents: currentCents, currency: next });
  };

  return (
    <HStack gap="3" align="center" wrap="wrap">
      <HStack
        gap="0"
        borderWidth="1px"
        borderStyle="dashed"
        rounded="lg"
        overflow="hidden"
        w="fit-content"
        borderColor={hasError ? 'fg.error' : 'perRecipient.border'}
        bg="perRecipient.surface"
      >
        {amountInputVariant === 'number' ? (
          <NumberInputRoot
            {...decimal.rootProps}
            min={0}
            step={0.01}
            clampValueOnBlur={false}
            value={amount}
            onValueChange={({ value: raw }) => commitAmount(raw)}
            invalid={hasError}
            w="130px"
          >
            <NumberInputField
              {...decimal.inputProps}
              border="none"
              bg="transparent"
              fontSize="15px"
              fontWeight="semibold"
              color="perRecipient.text"
              placeholder={
                placeholder ?? t('collabs.briefTemplates.sections.compensation.amountPlaceholder')
              }
              data-testid={`${testIdPrefix}-amount`}
              _focusVisible={{ outline: 'none' }}
            />
            <NumberInputControl>
              <NumberInputIncrementTrigger aria-label={t('common.increase')} />
              <NumberInputDecrementTrigger aria-label={t('common.decrease')} />
            </NumberInputControl>
          </NumberInputRoot>
        ) : (
          <Input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            pattern="[0-9]*[.,]?[0-9]*"
            value={amount}
            onChange={(event) => commitAmount(event.currentTarget.value)}
            w="130px"
            border="none"
            bg="transparent"
            fontSize="15px"
            fontWeight="semibold"
            color="perRecipient.text"
            aria-invalid={hasError || undefined}
            placeholder={
              placeholder ?? t('collabs.briefTemplates.sections.compensation.amountPlaceholder')
            }
            data-testid={`${testIdPrefix}-amount`}
            _focusVisible={{ outline: 'none' }}
          />
        )}
        <CurrencyControl
          currency={currency}
          currencies={currencies}
          onSelect={selectCurrency}
          testId={`${testIdPrefix}-currency`}
        />
      </HStack>
      <Box maxW="260px">
        <Text fontSize="12px" color="fg.muted" lineHeight="1.4">
          {note ?? t('collabs.briefTemplates.sections.compensation.paymentNote')}
        </Text>
      </Box>
    </HStack>
  );
};
